import {
  TrialPopupType,
  SignInUpMethod,
  UseCase,
  FileInteractionLocation,
  FileDownloadStatus,
  CustomerCategory,
  LogoStatus,
} from './literal-types';

export enum PlatformName {
  GTM = 'GTM',
}

export type EventsPayload = {
  user_data: {
    [PlatformName.GTM]: {
      user_id: number;
      user_usecase: UseCase;
      customer_category: CustomerCategory;
    };
  };
  sign_up: {
    [PlatformName.GTM]: {
      method: SignInUpMethod;
      accounts_on_device: number;
      send_marketing_email?: 'allow' | 'deny';
      user_id: number;
    };
  };
  sign_up_v2: {
    [PlatformName.GTM]: {
      method: SignInUpMethod;
      accounts_on_device: number;
      send_marketing_email?: 'allow' | 'deny';
    };
  };
  onboarding_submit: {
    [PlatformName.GTM]: {
      usecase: UseCase;
    };
  };
  login: {
    [PlatformName.GTM]: {
      method: SignInUpMethod;
    };
  };
  trial_popup_close: {
    [PlatformName.GTM]: {
      popup_name: TrialPopupType;
    };
  };
  trial_popup_to_checkout: {
    [PlatformName.GTM]: {
      popup_name: TrialPopupType;
    };
  };
  payment_refuse_survey_close: {
    [PlatformName.GTM]: Record<string, never>;
  };
  payment_refuse_survey_to_checkout: {
    [PlatformName.GTM]: Record<string, never>;
  };
  payment_refuse_survey_answer: {
    [PlatformName.GTM]: {
      payment_refuse_reason: string;
    };
  };
  click_generation_start: {
    [PlatformName.GTM]: {
      style_name: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      font: string;
      logo: LogoStatus;
      processed_file_id: string;
    };
  };
  style_change_save: {
    [PlatformName.GTM]: {
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      style_name: string;
      font: string;
      logo: LogoStatus;
      processed_file_id?: string;
    };
  };
  service_error_system: {
    [PlatformName.GTM]: {
      action: string;
      fileID?: string;
      httpCode?: number;
      message: string;
      errorCode?: string; // Код серверной ошибки
      url?: string; // URL запроса, который вызвал ошибку
      method?: string; // HTTP метод запроса
    };
  };
  service_error_preview: {
    [PlatformName.GTM]: {
      message: string;
      action: string;
      class: string;
      inner_message: string;
      trace: string;
    };
  };
  generation_slide_start: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      interaction_location: 'advanced_mode' | 'raw_mode';
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  generation_slide_done: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      interaction_location: 'advanced_mode' | 'raw_mode';
    };
  };
  generation_add_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  generation_delete_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  generation_duplicate_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  generation_move_slide: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  design_editing: {
    [PlatformName.GTM]: {
      editing_location: 'preview_interaction' | 'menu_interaction';
      editable_element: string;
      processed_file_id: string;
    };
  };
  design_editing_canvas: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  click_file_download: {
    [PlatformName.GTM]: {
      file_interaction_location: FileInteractionLocation;
      file_type: 'pptx' | 'pdf';
      file_slides_count: number;
      style: string;
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  file_download: {
    [PlatformName.GTM]: {
      file_download_status: FileDownloadStatus;
      redirect_place?: 'confirm_email';
      file_interaction_location: FileInteractionLocation;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      file_type: 'pptx' | 'pdf';
      processed_file_id: string;
    };
  };
  file_create_duplicate: {
    [PlatformName.GTM]: Record<string, never>;
  };
  file_rename: {
    [PlatformName.GTM]: {
      file_interaction_location: FileInteractionLocation;
    };
  };
  file_delete: {
    [PlatformName.GTM]: Record<string, never>;
  };
  slideshow_started: {
    [PlatformName.GTM]: {
      processed_file_id: string;
      file_interaction_location: 'shared_page' | 'workspace';
    };
  };
  share: {
    [PlatformName.GTM]: {
      method: 'link_access' | 'pin_access';
      file_url: string;
      file_interaction_location: FileInteractionLocation;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  begin_checkout: {
    [PlatformName.GTM]: {
      ecommerce: {
        currency: string;
        value: number;
        payment_method: string;
        product_id: number;
        items: Array<{
          item_name: 'subscription' | 'pay_as_you_go' | 'ai_tokens';
          item_variant: string;
          price: number;
          quantity: number;
        }>;
      };
    };
  };
  generation_start: {
    [PlatformName.GTM]: {
      event: 'generation_start';
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  generation_first_slide_done: {
    [PlatformName.GTM]: {
      event: 'generation_first_slide_done';
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
    };
  };
  generation_done: {
    [PlatformName.GTM]: {
      event: 'generation_done';
      processed_file_id: string;
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      interaction_location?: 'advanced_mode' | 'raw_mode';
    };
  };
  chat_submit: {
    [PlatformName.GTM]: {
      event: 'chat_submit';
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      slides_count?: number;
    };
  };
  cards_submit: {
    [PlatformName.GTM]: {
      event: 'cards_submit';
      flow: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | 'unknown';
      slides_count: number;
    };
  };
  click_plan: {
    [PlatformName.GTM]: {
      plan:
        | 'monthly_starter'
        | 'monthly_basic'
        | 'monthly_expert'
        | 'yearly_starter'
        | 'yearly_basic'
        | 'yearly_expert'
        | 'payg'
        | 'ai_tokens';
    };
  };
  click_upgrade_to_pro: {
    [PlatformName.GTM]: Record<string, never>;
  };
  click_new_version_banner: {
    [PlatformName.GTM]: {
      is_authenticated: boolean;
      target_url: string;
    };
  };
  dismiss_new_version_banner: {
    [PlatformName.GTM]: {
      is_authenticated: boolean;
    };
  };
};

export type EventTriggerNames = keyof EventsPayload;
